import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, Check } from 'lucide-react';
import './ServiceDetail.css';

const ServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  const services = {
    'brand-solutions': {
      title: 'Brand Solutions',
      tagline: 'You know what your brand needs to say. We figure out how it needs to speak.',
      description: 'We provide customised solutions to meet partner needs. We understand the objectives and requirements to develop strategies & create holistic consumer experiences and fully serve clients.',
      longDescription: 'At Team FullStack, we believe that every brand has a unique story waiting to be told. Our Brand Solutions service is designed to help you articulate that story in a way that resonates deeply with your audience. We combine strategic thinking with creative execution to build brands that not only look great but also drive meaningful business results.',
      features: [
        'Brand Strategy & Positioning',
        'Visual Identity Design',
        'Brand Guidelines Development',
        'Brand Communication Strategy',
        'Market Research & Analysis',
        'Competitive Brand Audits',
      ],
      benefits: [
        'Increased brand recognition and recall',
        'Stronger emotional connection with your audience',
        'Consistent brand experience across all touchpoints',
        'Differentiated positioning in the market',
      ],
    },
    'tech-solutions': {
      title: 'Tech Solutions',
      tagline: 'Transforming businesses through innovative technology solutions.',
      description: 'We optimise People, Processes and Technology. Map processes, align to objectives and maximise team efficiency with tech to improve the customer experience.',
      longDescription: 'In today\'s digital-first world, technology is the backbone of every successful business. Our Tech Solutions service helps you leverage cutting-edge technology to streamline operations, enhance productivity, and deliver exceptional customer experiences. We work closely with your team to understand your unique challenges and design solutions that drive real business value.',
      features: [
        'Enterprise Software Development',
        'Cloud Infrastructure & Migration',
        'API Integration & Development',
        'System Architecture Design',
        'Process Automation',
        'Technology Consulting',
      ],
      benefits: [
        'Improved operational efficiency',
        'Reduced costs through automation',
        'Scalable technology infrastructure',
        'Enhanced security and compliance',
      ],
    },
    'media-solutions': {
      title: 'Media Solutions',
      tagline: 'We ensure your brand speaks to your audience where they are most likely to be engaged.',
      description: 'Drive results through strategic, audience-aligned ad placement across channels using the right messaging at the optimal time.',
      longDescription: 'Effective media planning and buying can make or break your marketing campaigns. Our Media Solutions service combines data-driven insights with creative strategy to ensure your brand reaches the right audience, at the right time, with the right message. We manage campaigns across all major platforms and channels, optimizing for performance and ROI.',
      features: [
        'Media Planning & Strategy',
        'Programmatic Advertising',
        'Social Media Advertising',
        'Search Engine Marketing',
        'Display & Video Advertising',
        'Campaign Performance Analytics',
      ],
      benefits: [
        'Higher ROI on advertising spend',
        'Better audience targeting and reach',
        'Optimized campaign performance',
        'Comprehensive performance reporting',
      ],
    },
    'research-solutions': {
      title: 'Research Solutions',
      tagline: 'Data-driven insights that shape strategic decisions.',
      description: 'We provide specialised market research for businesses using qualitative and quantitative methods. We integrate market research with our services to connect research, strategy and implementation.',
      longDescription: 'Informed decisions require deep insights. Our Research Solutions service provides you with the data and analysis you need to understand your market, customers, and competitive landscape. We combine quantitative and qualitative research methods to deliver actionable insights that drive strategic decision-making.',
      features: [
        'Market Research & Analysis',
        'Consumer Behavior Studies',
        'Competitive Intelligence',
        'Brand Perception Studies',
        'Customer Satisfaction Surveys',
        'Data Analytics & Reporting',
      ],
      benefits: [
        'Data-driven decision making',
        'Better understanding of your audience',
        'Competitive advantage through insights',
        'Reduced risk in strategic initiatives',
      ],
    },
    'web-development': {
      title: 'Web Development',
      tagline: 'We have an unhealthy obsession with grabbing eyeballs, and we\'re not really sorry about it either.',
      description: 'Custom web applications and websites built with modern technologies. From concept to deployment, we deliver scalable solutions that drive business growth.',
      longDescription: 'Your website is often the first impression customers have of your brand. Our Web Development service creates stunning, high-performance websites and web applications that not only look incredible but also deliver exceptional user experiences. We use the latest technologies and best practices to build solutions that are fast, secure, and scalable.',
      features: [
        'Custom Website Development',
        'E-commerce Solutions',
        'Web Application Development',
        'Progressive Web Apps (PWA)',
        'CMS Integration',
        'Performance Optimization',
      ],
      benefits: [
        'Improved user experience',
        'Higher conversion rates',
        'Mobile-responsive design',
        'SEO-friendly architecture',
      ],
    },
    'generative-ai': {
      title: 'Generative AI',
      tagline: 'Any time you feel like you need a bigger megaphone, we\'ll get your brand a voice that\'s sonorous…',
      description: 'Leverage cutting-edge AI to automate processes, generate creative content, and unlock valuable insights from your data.',
      longDescription: 'Artificial Intelligence is transforming how businesses operate and create value. Our Generative AI service helps you harness the power of AI to automate workflows, generate creative content, analyze data, and create personalized experiences at scale. We work with the latest AI technologies to deliver solutions that drive innovation and efficiency.',
      features: [
        'AI-Powered Content Generation',
        'Process Automation with AI',
        'Predictive Analytics',
        'Natural Language Processing',
        'Computer Vision Solutions',
        'AI Strategy & Consulting',
      ],
      benefits: [
        'Increased productivity and efficiency',
        'Cost reduction through automation',
        'Enhanced creativity and innovation',
        'Personalized customer experiences',
      ],
    },
    'delegation-management': {
      title: 'Delegation Management',
      tagline: 'Seamless coordination for successful business delegations.',
      description: 'We manage delegations for a wide range of business groups where we facilitate the logistics, setup meetings and organize everything. From planning to execution, we ensure seamless coordination for your business delegations.',
      longDescription: 'Successful business delegations require meticulous planning, seamless coordination, and flawless execution. Our Delegation Management service handles every aspect of your business delegation needs. We manage delegations for a wide range of business groups, facilitating logistics, setting up meetings, and organizing everything from travel arrangements to venue bookings. Our experienced team ensures that every detail is taken care of, allowing you to focus on what matters most - your business objectives.',
      features: [
        'Delegation Planning & Coordination',
        'Logistics Management',
        'Meeting Setup & Organization',
        'Travel Arrangements',
        'Venue Booking & Management',
        'Event Coordination',
        'Documentation & Reporting',
        'Multi-stakeholder Communication',
      ],
      benefits: [
        'Seamless delegation execution',
        'Time savings through professional coordination',
        'Reduced stress and administrative burden',
        'Professional representation of your business',
        'Comprehensive support throughout the delegation',
      ],
    },
    'product-shoots': {
      title: 'Professional Product Shoots',
      tagline: 'Making your products look as good as they deserve.',
      description: 'We create stunning product photography that showcases your products in the best light. Our professional product shoots capture every detail, making your products irresistible to customers.',
      longDescription: 'In today\'s visual-first marketplace, exceptional product photography is essential for success. Our Professional Product Shoots service delivers stunning, high-quality images that showcase your products in their best light. We combine professional photography techniques with creative styling to create images that not only look beautiful but also drive sales. From e-commerce to marketing materials, our product photography helps your products stand out and connect with customers.',
      features: [
        'Studio Product Photography',
        'Lifestyle Product Shoots',
        'E-commerce Product Images',
        '360° Product Views',
        'Packaging Photography',
        'Creative Styling & Art Direction',
        'Post-Production & Retouching',
        'Multi-angle Product Shots',
      ],
      benefits: [
        'Increased product appeal and sales',
        'Professional, high-quality imagery',
        'Consistent brand visual identity',
        'Optimized for e-commerce platforms',
        'Enhanced customer confidence',
      ],
    },
  };

  const service = services[id] || services['brand-solutions'];
  const allServices = Object.keys(services);

  useEffect(() => {
    if (!services[id]) {
      navigate('/services');
    }
  }, [id, navigate]);

  return (
    <div className="service-detail-page">
      <section className="service-hero">
        <div className="container">
          <motion.div
            className="service-hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link to="/services" className="back-link">
              <ArrowRight size={20} style={{ transform: 'rotate(180deg)' }} /> Back to Services
            </Link>
            <h1 className="service-title">{service.title}</h1>
            <p className="service-tagline-large">{service.tagline}</p>
          </motion.div>
        </div>
      </section>

      <section className="service-content" ref={ref}>
        <div className="container">
          <div className="service-layout">
            <aside className="service-sidebar">
              <h3>Our Services</h3>
              <nav className="services-nav">
                {allServices.map((serviceId) => (
                  <Link
                    key={serviceId}
                    to={`/services/${serviceId}`}
                    className={`service-nav-link ${id === serviceId ? 'active' : ''}`}
                  >
                    {services[serviceId].title}
                  </Link>
                ))}
              </nav>
            </aside>

            <main className="service-main">
              <motion.div
                className="service-description-section"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8 }}
              >
                <h2>Overview</h2>
                <p className="service-description-text">{service.longDescription}</p>
              </motion.div>

              <motion.div
                className="service-features"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <h2>What We Offer</h2>
                <div className="features-grid">
                  {service.features.map((feature, index) => (
                    <div key={index} className="feature-item">
                      <Check size={24} className="feature-icon" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                className="service-benefits"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <h2>Key Benefits</h2>
                <ul className="benefits-list">
                  {service.benefits.map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                className="service-cta"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <h2>Ready to get started?</h2>
                <p>Let's create something extraordinary together.</p>
                <Link to="/contact" className="btn-cta">
                  Get in Touch <ArrowRight size={20} />
                </Link>
              </motion.div>
            </main>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
